import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AppRole, DEFAULT_ROLE, ROLE_DASHBOARDS } from '@/constants/roles';

type AuthResult = { error: AuthError | Error | null };
type SignInResult = AuthResult & { redirectTo?: string };

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, phone?: string) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthResult>;
  updatePassword: (password: string) => Promise<AuthResult>;
  userRole: AppRole | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Normalize any thrown value into an Error with a message. Avoids `any` in
 * catch blocks while still surfacing the underlying message.
 */
const toError = (err: unknown): Error => {
  if (err instanceof Error) return err;
  if (typeof err === 'string') return new Error(err);
  return new Error('Unexpected error');
};

const messageOf = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message: unknown }).message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
  }
  return fallback;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<AppRole | null>(null);

  useEffect(() => {
    // Resolve initial session only to set `loading` to false on mount.
    // The auth state listener (onAuthStateChange) handles all subsequent
    // auth events and is the single source of truth for user role fetching
    // (was previously double-fetched via both paths). This removes the
    // N+1 query that previously ran on every page load.
    supabase.auth.getSession().finally(() => setLoading(false));

    // Set up auth state listener. INITIAL_SESSION fires immediately.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        setSession(nextSession);
        setUser(nextSession?.user ?? null);

        if (nextSession?.user) {
          // Fire-and-forget; the in-flight fetch updates the role state.
          void fetchUserRole(nextSession.user.id);
        } else {
          setUserRole(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        setUserRole(DEFAULT_ROLE);
        return;
      }

      setUserRole((data?.role as AppRole) || DEFAULT_ROLE);
    } catch (err) {
      console.error('Error fetching user role:', err);
      setUserRole(DEFAULT_ROLE);
    }
  };

  const checkRateLimit = async (
    email: string,
    actionType: 'login' | 'signup' | 'reset_password'
  ): Promise<{ allowed: boolean; attemptId: string | null; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke('check-rate-limit', {
        body: { email, action_type: actionType, step: 'check' }
      });

      if (error) {
        console.error('Rate limit check error:', error);
        return { allowed: true, attemptId: null };
      }

      if (data && data.allowed === false) {
        return { allowed: false, attemptId: null, error: data.error };
      }

      return { allowed: true, attemptId: data?.attempt_id || null };
    } catch (err) {
      console.error('Rate limit check exception:', err);
      return { allowed: true, attemptId: null };
    }
  };

  const updateRateLimit = async (attemptId: string | null, isSuccessful: boolean, email?: string) => {
    if (!attemptId) return;
    try {
      await supabase.functions.invoke('check-rate-limit', {
        body: { attempt_id: attemptId, is_successful: isSuccessful, step: 'update', email }
      });
    } catch (err) {
      console.error('Failed to update rate limit state:', err);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    phone?: string
  ): Promise<AuthResult> => {
    let attemptId: string | null = null;
    try {
      const limitRes = await checkRateLimit(email, 'signup');
      if (!limitRes.allowed) {
        const errorMsg = limitRes.error || 'Quá nhiều yêu cầu đăng ký. Vui lòng thử lại sau.';
        toast.error(errorMsg);
        return { error: new Error(errorMsg) };
      }
      attemptId = limitRes.attemptId;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            phone: phone || null,
          }
        }
      });

      if (error) throw error;

      await updateRateLimit(attemptId, true);
      toast.success('Đăng ký thành công! Vui lòng đăng nhập.');
      return { error: null };
    } catch (err) {
      await updateRateLimit(attemptId, false);
      const msg = messageOf(err, 'Đăng ký thất bại');
      toast.error(msg);
      return { error: toError(err) };
    }
  };

  const resetPassword = async (email: string): Promise<AuthResult> => {
    let attemptId: string | null = null;
    try {
      const limitRes = await checkRateLimit(email, 'reset_password');
      if (!limitRes.allowed) {
        const errorMsg = limitRes.error || 'Quá nhiều yêu cầu khôi phục mật khẩu. Vui lòng thử lại sau.';
        toast.error(errorMsg);
        return { error: new Error(errorMsg) };
      }
      attemptId = limitRes.attemptId;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`,
      });
      if (error) throw error;

      await updateRateLimit(attemptId, true);
      toast.success('Email đặt lại mật khẩu đã được gửi!');
      return { error: null };
    } catch (err) {
      await updateRateLimit(attemptId, false);
      const msg = messageOf(err, 'Gửi yêu cầu thất bại');
      toast.error(msg);
      return { error: toError(err) };
    }
  };

  const updatePassword = async (password: string): Promise<AuthResult> => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('Đặt lại mật khẩu thành công!');
      return { error: null };
    } catch (err) {
      const msg = messageOf(err, 'Đặt lại mật khẩu thất bại');
      toast.error(msg);
      return { error: toError(err) };
    }
  };

  const signIn = async (email: string, password: string): Promise<SignInResult> => {
    let attemptId: string | null = null;
    try {
      const limitRes = await checkRateLimit(email, 'login');
      if (!limitRes.allowed) {
        const errorMsg = limitRes.error || 'Quá nhiều yêu cầu đăng nhập. Vui lòng thử lại sau.';
        toast.error(errorMsg);
        return { error: new Error(errorMsg) };
      }
      attemptId = limitRes.attemptId;

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Fetch user role and return redirect path
      let redirectTo = ROLE_DASHBOARDS[DEFAULT_ROLE];

      if (data.user) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', data.user.id)
          .maybeSingle();

        const role = (roleData?.role as AppRole) || DEFAULT_ROLE;
        setUserRole(role);
        redirectTo = ROLE_DASHBOARDS[role] || ROLE_DASHBOARDS[DEFAULT_ROLE];
      }

      await updateRateLimit(attemptId, true, email);
      toast.success('Đăng nhập thành công!');
      return { error: null, redirectTo };
    } catch (err) {
      await updateRateLimit(attemptId, false);
      const msg = messageOf(err, 'Đăng nhập thất bại');
      toast.error(msg);
      return { error: toError(err) };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUserRole(null);
      toast.success('Đăng xuất thành công!');
    } catch (err) {
      console.error('Sign out error:', err);
      toast.error('Đăng xuất thất bại');
    }
  };

  // Memoize context value so consumers don't re-render on every parent render.
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      updatePassword,
      userRole,
    }),
    [user, session, loading, userRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
